import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  Subject,
  delay,
  filter,
  map,
  of,
  startWith,
  take,
  takeUntil,
} from 'rxjs';
import { trim } from 'lodash';
import { AppFieldValidators } from 'src/app/utilities/validators';
import {
  DropDownListComponent,
} from '@syncfusion/ej2-angular-dropdowns';

import { ErrorStateMatcher } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { MatRadioGroup } from '@angular/material/radio';
import { LogService } from '@services/log.service';

export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && control.enabled && control.parent?.enabled &&  (control?.dirty || control?.touched));
  }
}


export type TapFormFieldType =
  | 'autocomplete'
  //| 'address'
  | 'hidden'
  | 'text'
  | 'number'
  | 'email'
  | 'password'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week'
  | 'url'
  | 'tel'
  | 'color'
  | 'label'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'toggle'
  | 'radio'
  | 'radio-group'
  | 'slide-toggle'
  | 'checkbox-list'
  | 'ejs-dropdown'
  | 'link';

@Component({
  selector: 'app-form-field',
  templateUrl: './tap-form-field.component.html',
  styleUrls: ['./tap-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TapFormFieldComponent
  implements OnInit, AfterContentInit, OnDestroy, AfterViewInit
{
  immediateErrorStateMatcher = new ImmediateErrorStateMatcher();
  _formStateApplied = false;

  @ViewChild('inputElement') inputElement: ElementRef;
  @ViewChild('dropdownlist') dropdownlist: DropDownListComponent;

  @Input() fieldLabel: string;
  @Input() fieldId: string;
  @Input() fieldControlName: string;
  @Input() isRequired: boolean;
  @Input() formGroup: UntypedFormGroup;
  @Input() localizeOptions: boolean = true;

  /**
   * `fieldId` is also used (by consumers, via a plain `id`/`class` attribute on the
   * `<app-form-field>` host) as a CSS grid-area name for form layout. The actual
   * control and its label must not reuse that same id, or the host and the control
   * collide on one id and `<label for>` resolves to the first (non-labelable) match
   * instead of falling through to the control - so clicking the label does nothing.
   * These derive distinct, collision-free ids from `fieldId` for the real control and
   * its label.
   */
  get inputId(): string {
    return this.fieldId ? `${this.fieldId}_input` : this.fieldId;
  }

  get labelId(): string {
    return this.fieldId ? `${this.fieldId}_label` : this.fieldId;
  }

  /**
   * `<mat-select>`, `<mat-radio-group>`, `<mat-slide-toggle>` and `<mat-selection-list>` are not
   * part of the HTML "labelable" element list (only `<input>`, `<button>`, `<select>`,
   * `<textarea>` etc. are), so a browser will not forward a native label click to them even when
   * `for`/`id` match, unlike a real `<input>` or `<mat-checkbox>` (which already wraps a real
   * `<input type="checkbox">` its own internal label owns). The radio-group template calls this
   * directly since MatRadioGroup itself has no `focus()`; the other control types call their own
   * `focus()`/`open()` straight from the template.
   */
  focusRadioGroup(radioGroup: MatRadioGroup): void {
    (radioGroup.selected ?? radioGroup._radios?.first)?.focus();
  }
  @Output() selectionChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() valueChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() keyup: EventEmitter<any> = new EventEmitter<any>();
  @Output() fieldClick: EventEmitter<any> = new EventEmitter<any>();
  @Input() suffix: string | null = null;
  @Input() prefix: string | null = null;
  @Input() allowFiltering: boolean = true;
  //Passed straight through to app-dropdown-form-field (type="ejs-dropdown" only).
  @Input() itemTemplate: any = undefined;
  @Input() valueTemplate: any = undefined;
  @Input() enableVirtualization: boolean = true;

  /**
   * Passed to app-dropdown-form-field: a spinner over the control while its OPTIONS are being
   * fetched. Only meaningful for type="ejs-dropdown".
   */
  @Input() loading: boolean = false;
  @Input() tabIndex: number | null = null;
  private _calcValue: string | null = null;
  get calcValue(): string | null {
    return this._calcValue;
  }
  @Input() set calcValue(value: string | null){
    this.logger.log(this.fieldControlName +  ' set calcValue ' + value, value);
    this._calcValue = value;
  };

  @Input() calcSuffix: string | null = null;
  @Input() placeholder: string | null = null;
  @Input() labelPosition: 'before' | 'after' | null = 'after';
  @Input() valueLabel: string | null = null;

  /**
   * Optional qualifier rendered beside the value on a read-only field - already-translated text, not
   * a resource key. Additive: callers that do not set it render exactly as before.
   *
   * Used for "Source Org" on Move Locations, where the qualifier belongs to the ORGANIZATION shown
   * rather than to the field, so baking it into labelValue would make it part of the name.
   */
  @Input() labelValueSuffix: string | null = null;
  @Input() fieldLayout: 'row' | 'column' = 'row';
  @Input() appearance: 'legacy' | 'standard' | 'fill' | 'outline' = 'outline';
  @Input() indeterminate: boolean = false;
  @Input() showLabelValueInError: boolean = false;

  @Input() debugMode: boolean = false;

  Validators = AppFieldValidators;

  private _disabled: boolean = false;
  private __clearOptions$ = new Subject();
  private _formState?: any = null;
  private destroy$: Subject<void> = new Subject<void>();
  private _validatorOrOpts?:
    | ValidatorFn
    | ValidatorFn[]
    | AbstractControlOptions
    | null = null;

  protected readonly selectOptionfields = {
    text: 'text',
    value: 'value',
  };

  currentLatitude: number;
  currentLongitude: number;

  public get disabled(): boolean {
    return this._disabled;
  }
  @Input()
  public set disabled(value: boolean) {
    //debugger;
    if (value === null || value === undefined || this.disabled === value)
      return;

    this._disabled = value;
    if (this.formControl) {
      if (this.formGroup.disabled || value) {
        this.formControl.disable({ onlySelf: true, emitEvent: false });
      } else {
        this.formControl.enable({ onlySelf: true, emitEvent: false });
      }
    }
  }

  public get formState(): any {
    return this._formState;
  }
  @Input()
  public set formState(value: any) {
    this.logger.log(this.fieldControlName +  ' set formState ' + value, value);
    this._formState = value;
    if (this.formControl) {
      this._formStateApplied = true;
    }
    this.setControlValue(value);
  }

  public get validatorOrOpts():
    | ValidatorFn
    | ValidatorFn[]
    | AbstractControlOptions
    | null {
    return this._validatorOrOpts;
  }
  @Input()
  public set validatorOrOpts(
    value: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
  ) {
    let requiredIsAlreadyDefined = false;

    if (this._validatorOrOpts && Array.isArray(this._validatorOrOpts)) {
      //debugger;

      const validators =
        this._validatorOrOpts?.['validators'] ?? this._validatorOrOpts;
      const requiredValidator = validators.find((v) => v?.name === 'required');
      if (requiredValidator) requiredIsAlreadyDefined = true;
    } else if (this._validatorOrOpts && !Array.isArray(this._validatorOrOpts)) {
      if (value['name'] !== 'required') {
        requiredIsAlreadyDefined = true;
      }
    }
    this._validatorOrOpts = value;
    let includesRequired = false;
    //test if required Validator is present and if so set isRequired to true
    if (value && Array.isArray(value)) {
      const requiredValidator = value.find((v) => v?.name === 'required');
      if (requiredValidator) {
        includesRequired = true;
      }
    } else if (value && typeof value === 'function') {
    includesRequired = (value as any)?.name === 'required';
  } else if (value && (value as AbstractControlOptions)?.validators) {
    const validators = (value as AbstractControlOptions).validators;
    if (Array.isArray(validators)) {
      includesRequired = validators.some((v) => v?.name === 'required');
    } else if (validators && (validators as any).name === 'required') {
      includesRequired = true;
    }
  }

    //remove if required was already defined and not included in new validator(s)
    if (!includesRequired && requiredIsAlreadyDefined) this.isRequired = false;
    else if (includesRequired) this.isRequired = true;

    const ctrl = this.existingControl();
    if (ctrl) {
      const toArray = <T>(validators: T | T[] | null | undefined): T[] => {
        if (!validators) {
          return [];
        }
        return Array.isArray(validators) ? validators : [validators];
      };

      if (typeof value === 'function' || Array.isArray(value)) {
        // case: ValidatorFn | ValidatorFn[]
        ctrl.setValidators(value as ValidatorFn | ValidatorFn[]);
        const inputAsyncValidators = toArray(this.asyncValidator);
        ctrl.setAsyncValidators(inputAsyncValidators.length > 0 ? inputAsyncValidators : null);
      } else if (value && (value as AbstractControlOptions).validators) {
        // case: AbstractControlOptions
        const options = value as AbstractControlOptions;
        ctrl.setValidators(options.validators ?? null);

        const inputAsyncValidators = toArray(this.asyncValidator);
        const optionAsyncValidators = toArray(options.asyncValidators as AsyncValidatorFn | AsyncValidatorFn[] | null | undefined);
        const mergedAsyncValidators = [...optionAsyncValidators, ...inputAsyncValidators];
        ctrl.setAsyncValidators(mergedAsyncValidators.length > 0 ? mergedAsyncValidators : null);
      } else {
        ctrl.clearValidators();
        const inputAsyncValidators = toArray(this.asyncValidator);
        ctrl.setAsyncValidators(inputAsyncValidators.length > 0 ? inputAsyncValidators : null);
      }

      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  @Input() asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null;

  private _col: number | string = 12; //form fields should resolve to 100% by default.
  public get col(): number | string {
    return this._col;
  }
  @Input()
  public set col(value: number | string) {
    this._col = value;
  }
  @Input() labelValue: number | string | null = null;

  private _options: { text: string; value: any }[] = [];
  public get options(): { text: string; value: any }[] {
    return this._options;
  }
  @Input()
  public set options(value: { text: string; value: any }[]) {
    if (this.debugMode) {
      this.logger.log('setting options for ', this.fieldControlName, value);
    }
    this._options = value;

    //if drop-down type and control exists, set the selected option
    if (this.type === 'ejs-dropdown')
        return;

    this.__options = new BehaviorSubject<any[]>([]);
    this.selectScrollDataOffset = 0;

    if (this.type === 'autocomplete') {
      const getFiltered = (value: string) => {
        if (!value || (this.options.length > 1000 && value.length < 3)) return [];
        return this.options.filter((o) => o.text.toLowerCase().includes(value.toLowerCase()));
      };
      const currentVal = typeof this.formControl?.value === 'string' ? this.formControl.value : '';
      this.options$ = this.formControl?.valueChanges.pipe(
        filter((value) => typeof value === 'string'),
        map((value) => getFiltered(value)),
        startWith(getFiltered(currentVal)),
      );
    } else {
      //setup infinity scroll. getNextBatch accumulates into the subject rather than the stream
      //accumulating with scan: a BehaviorSubject replays only its LAST value, so any batch emitted
      //before the template subscribed was dropped from the scan and those options never rendered.
      this.options$ = this.__options
        .asObservable()
        .pipe(takeUntil(this.destroy$));
    }
    this.getNextBatch(this.batchesToReachSelectedValue());
  }

  _type: TapFormFieldType = 'text';

  get type(): TapFormFieldType {
    return this._type;
  }

  @Input()
  set type(value: TapFormFieldType) {
    this._type = value;
  }

  private _className: string = '';
  public get className(): string {
    return this._className || '';
  }
  @Input()
  set className(value: string) {
    this._className = value;
  }

  /**
   * A getter rather than a field, because the field form was written once at construction - before
   * any @Input had bound - and then rewritten by `set col` as
   * `'d-flex flex-column col-md-' + col`, which dropped `className` on the floor. Every field that
   * set `col` (i.e. nearly all of them) silently discarded whatever `className` its caller passed,
   * and the two inputs' binding order decided the outcome. As a getter it is recomputed on change
   * detection, so both inputs always contribute and neither can clobber the other.
   */
  @HostBinding('class') get myClass(): string {
    return (
      'd-flex flex-column ' +
      (this.col ? 'col-md-' + this.col : '') +
      ' ' +
      this.className
    ).trim();
  }

  formControl: UntypedFormControl;

  /**
   * The control registered under `fieldControlName`, whether this component created it or the
   * consumer pre-created it on the form group.
   *
   * Deliberately NOT `FormGroup.contains()`, which this used to be. `contains()` is
   * `hasOwnProperty(name) && controls[name].enabled` - it answers false for a control the consumer
   * created and then disabled, which is an ordinary thing to do before the field first renders.
   * Read as an existence check it made this component build a SECOND control for that same name,
   * carrying the validators; `addControl` then silently dropped it, because `registerControl`
   * returns the already-registered control without re-parenting the new one. The result was an
   * orphan that was never part of the form, yet still ran its async validators and wrote their
   * errors onto the real control - a validation failure on a field the user cannot even edit.
   */
  private existingControl(): AbstractControl | null {
    if (!this.formGroup || !this.fieldControlName) return null;
    return this.formGroup.get(this.fieldControlName);
  }

  /**
   * A disabled control cannot be corrected by whoever is looking at it, so a validation message on
   * one is noise. `<mat-error>` here renders straight off the `errorText` input and never consults
   * the `ErrorStateMatcher` that would already have suppressed it - the matcher requires
   * `control.enabled`, which is why such a field shows a red outline while its input still reports
   * `aria-invalid="false"`.
   */
  get showErrorText(): boolean {
    return !!this.errorText && (this.formControl ?? this.existingControl())?.disabled !== true;
  }

  get value(): any {
    return this.formControl?.value;
  }

  @Input()
  set value(v: any) {
    this.formControl?.setValue(v);
    //if drop-down type and control exists, set the selected option
    this.setControlValue(v);
  }

  get isValueFromAutoComplete(): boolean {
    return this.formControl?.['isValueFromAutoComplete'];
  }

  set isValueFromAutoComplete(v: boolean) {
    this.formControl['isValueFromAutoComplete'] = v;
  }

  @Input() errorText: string | null = null;
  @Input() warningText: string | null = null;

  /** Suppresses this field's own label. For composite controls (e.g. app-value-percent-field)
   *  that render one shared label across several inner fields. */
  @Input() hideLabel: boolean = false;

  /** Forces the invalid styling even when this control itself is valid. For composite controls
   *  whose inputs are two views of ONE value, so they flag as invalid together. */
  @Input() forceInvalid: boolean = false;

  /** Drops the standard 15px bottom margin. For checkbox fields that need to sit flush against
   *  the control below them (e.g. a checkbox stacked directly on the field it enables). */
  @Input() noBottomGap: boolean = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private translate: TranslateService,
    private logger: LogService
  ) {}

  ngAfterViewInit(): void {
    //this.setChildControlValue();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngAfterContentInit(): void {
    //try to set disabled on control.
    if (!this.formControl) return;

    this.formControl.statusChanges
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.changeDetectorRef.markForCheck());

    //Late pass to catch a disabled state that arrived after the control was created. Two rules:
    //  - read _disabled when the timer FIRES, not when it is scheduled. [disabled] is usually bound to
    //    async form data that has not arrived yet at this point, so a snapshot taken here is stale.
    //  - only ever disable. Enabling here would clobber controls disabled by other means (a [disabled]
    //    binding that already resolved, or a component calling .disable() on the control directly),
    //    and _disabled would then disagree with the control - a desync the setter below can never undo.
    of(null)
      .pipe(
        delay(10),
        switchMap(() => {
          return of(this._disabled === true || this.formGroup.disabled === true);
        }),
        take(1),
      )
      .subscribe((disabled) => {
        if (disabled === true) this.formControl.disable();
      });
  }

  ngOnInit(): void {
    this.createFormControl();
  }

  onSelectionChange($event): void {

    //if (!$event.isInteracted) return;

    //locate the option model and emit it
    const option = this.options.find((o) => o.value === $event.value);
    if (option) {
      this.selectionChange.emit(option);
    } else this.selectionChange.emit($event);
  }

  onKeyDown($event): void {
    if (this.type === 'autocomplete') {
      this.isValueFromAutoComplete = false;
    }
  }

  onKeyPress($event): void {
    //prevent non numeric input if the type is number, except for the decimal point.
    //allow backspace, delete, tab, escape, enter, and . (decimal point only only once in the string)
    //allow numbers to be entered with the keyboard
    if (this.type === 'number') {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        '.',
        'ArrowLeft',
        'ArrowRight',
        'Space',
        '-', //allow negative numbers
      ];
      if (
        !allowedKeys.includes($event.key) &&
        isNaN(Number($event.key)) &&
        $event.key !== ' '
      ) {
        $event.preventDefault();
      } else {
        //debugger;
        if ($event.key === '.') {
          if (this.formControl.value?.includes('.')) {
            $event.preventDefault();
          }
        } else if ($event.key === '-') {
          if (this.formControl.value?.includes('-')) {
            $event.preventDefault();
          }
        }
      }
    }
  }

  setControlValue(value: any): void {
    if (this.formControl) {
      //if this control is disabled, enable it to set the value
      const isDisabled = this.formControl.disabled;
      if (isDisabled) {
        this.formControl.enable({ onlySelf: true, emitEvent: false });
      }
      //set the value
      this.formControl.setValue(value);
      this.formControl['originalValue'] = this.formState;
      //if this control was disabled, disable it again
      if (isDisabled) {
        this.formControl.disable({ onlySelf: true, emitEvent: false });
      }

      //try to set the drop-down value
      this.trySetDropDownValue(value);
    }

    //outside the guard above: when the consumer pre-created the control on the form group,
    //this.formControl is never assigned, but mat-select still renders from the group's value.
    this.ensureSelectedOptionLoaded();
  }

  private trySetDropDownValue(value: any) {
    //if drop-down type and control exists, set the selected option
    if (this.type === 'ejs-dropdown' && this.dropdownlist) {
      this.logger.log('setting value for ', this.fieldControlName, this.dropdownlist.value, this.formState, this._options);
      this.dropdownlist.index = this.options.findIndex((o) => o.value === value);
      //this.dropdownlist.dataBind();
    }
  }


  public focus(): void {
    if (this.formControl && this.inputElement?.nativeElement) {
      this.inputElement.nativeElement.select()?.focus();
    }
  }

  //Infinity Scroll support properties/methods
  selectScrollDataOffset = 0;

  @Input() selectScrolBatchLimit = 100;

  __options = new BehaviorSubject<any[]>([]);
  options$: Observable<any[]> = null;

  /**
   * `selectScrolBatchLimit` is bound as an attribute in places (`selectScrolBatchLimit="{{n}}"`),
   * which hands it over as a string - and `offset + '300'` concatenates instead of adding.
   */
  private get batchLimit(): number {
    const limit = Number(this.selectScrolBatchLimit);
    return Number.isFinite(limit) && limit > 0 ? limit : 100;
  }

  getNextBatch(batchCount: number = 1) {
    if (!Array.isArray(this.options) || this.options.length === 0) return;

    const take = this.batchLimit * Math.max(1, batchCount);
    const result = this.options.slice(
      this.selectScrollDataOffset,
      this.selectScrollDataOffset + take,
    );
    if (result.length === 0) return;

    this.__options.next([...(this.__options.value ?? []), ...result]);
    this.selectScrollDataOffset += take;
  }

  /**
   * mat-select paints its closed-field text from the mat-option whose value matches the control, so
   * a value sitting past the loaded batch renders the field BLANK - no error, no fallback - until
   * the user scrolls the panel far enough to load it. Long lists (organizations, contacts) put the
   * selected value well past the first batch routinely. Load through the batch that contains it.
   */
  private batchesToReachSelectedValue(): number {
    if (!Array.isArray(this.options) || this.options.length === 0) return 1;

    const value =
      this.formGroup?.get(this.fieldControlName)?.value ?? this.formState;
    if (value === null || value === undefined) return 1;

    const index = this.options.findIndex((o) => o?.value === value);
    return index < 0 ? 1 : Math.floor(index / this.batchLimit) + 1;
  }

  /** Re-checks the above for a value that arrives after the options did. */
  private ensureSelectedOptionLoaded(): void {
    if (this.type === 'ejs-dropdown' || this.type === 'autocomplete') return;

    const loaded = Math.ceil(this.selectScrollDataOffset / this.batchLimit);
    const needed = this.batchesToReachSelectedValue();
    if (needed > loaded) this.getNextBatch(needed - loaded);
  }

  getSelectScrollComplete() {
    return this.selectScrollDataOffset >= this.options?.length;
  }

  onAutoCompleteOptionSelected($event: any) {
    this.selectionChange.emit($event.option.value);
  }

  displayFn(value: any): string {
    if (!value) return;

    //lookup
    const selectedOption = this._options.find((o) => o.value === value);
    if (!selectedOption) return value;

    this.formControl['isValueFromAutoComplete'] = true;

    //translate if needed, fallback to value
    if (this.localizeOptions) {
      if (selectedOption?.['selectedText'])
        return this.translate.instant(selectedOption?.['selectedText']);
      else if (selectedOption?.text)
        return this.translate.instant(selectedOption?.text);
      else return value;
    }

    if (selectedOption?.['selectedText'])
      return trim(selectedOption?.['selectedText']);
    else return trim(selectedOption?.text ?? value);
  }

  private createFormControl() {
    //create new form control
    if (this.type === 'label') return;

    if (this.formGroup && this.fieldControlName && !this.existingControl()) {
      const isParentDisabled = this.formGroup.disabled;
      this.formControl = new UntypedFormControl(
        { value: this.formState, disabled: this.disabled || isParentDisabled },
        this.validatorOrOpts,
      );
      this.formControl['originalValue'] = this.formState;

      //The constructor above has already run the validators once - before the line that assigns
      //originalValue existed to be read. An async validator that compares the current value against
      //originalValue to decide whether anything changed (the duplicate-name checks) therefore saw
      //undefined, could not short-circuit, and fired a server round trip for a value nobody edited.
      //Re-validating here cancels that in-flight async subscription - it is still inside its own
      //debounce timer, so the request never leaves - and re-runs with originalValue in place.
      //After construction rather than before, because `updateOn` is honoured only by the constructor.
      this.formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

      this.formGroup.addControl(this.fieldControlName, this.formControl);

      //set async validator if provided in input
      //this is needed to set the async validator after the control is created
      if (this.asyncValidator) {
        this.formControl.setAsyncValidators(this.asyncValidator);
      }

      //try to set disabled on control.
      // Passing in as option when creating does not work for
      // all controls.
      if (this.disabled === true || isParentDisabled === true) {
        this.formControl.disable({ onlySelf: true, emitEvent: false });
      }
    }

  }

  // onDataBound($event: any) {
  //   this.logger.log(`onDataSourceLoad for ${this.fieldControlName} | ${this.dropdownlist.value} | ${this.formState}`, $event, this.options);
  //   //see if the value is in the list before setting the value

  //   if($event.items === 0 || !this.options.some(i => i.value === this.formState))
  //   {
  //     this.dropdownlist.index = null;
  //     return;
  //   }

  //   //set the value of the control
  //   setTimeout(() => {
  //     this.logger.log(`setting value for ${this.fieldControlName} | ${this.dropdownlist.value} | ${this.formState}`, $event, this.options);
  //     this.trySetDropDownValue(this.formState);
  //     this.changeDetectorRef.markForCheck();
  //     //this.dropdownlist.refresh();
  //   }, 50);
  // }
}
